import { z } from 'zod';
import { Signal, type HvBand } from './enums';

export const LlmRegion = z
  .string()
  .trim()
  .regex(/^([a-z]+-[a-z]+\d+|us|eu)$/, 'Must be a specific GCP region (e.g. us-east1) or a multi-region (us, eu); "global" is not allowed');

export const Thresholds = z
  .object({
    high: z.number().int().min(1).max(100),
    medium: z.number().int().min(0).max(99),
  })
  .refine((t) => t.high > t.medium, { message: 'high threshold must be greater than medium' });
export type Thresholds = z.infer<typeof Thresholds>;

export const DEFAULT_THRESHOLDS: Thresholds = { high: 70, medium: 40 };

export function bandFor(score: number | null | undefined, thresholds: Thresholds): HvBand {
  if (score === null || score === undefined) return 'unscored';
  if (score >= thresholds.high) return 'high';
  if (score >= thresholds.medium) return 'medium';
  return 'low';
}

export const EvidenceItem = z.object({
  quote: z.string().trim().min(1).max(600),
  signal: Signal,
});
export type EvidenceItem = z.infer<typeof EvidenceItem>;

export const ScoringOutput = z.object({
  score: z.number().min(0).max(100).transform((n) => Math.round(n)),
  signals: z.array(Signal).max(5).transform((s) => [...new Set(s)]),
  evidence: z.array(EvidenceItem).max(20),
  rationale: z.string().trim().min(1).max(3000),
  confidence: z.number().min(0).max(1),
});
export type ScoringOutput = z.infer<typeof ScoringOutput>;

export const SCORING_OUTPUT_JSON_SCHEMA = {
  type: 'object',
  properties: {
    score: { type: 'integer', minimum: 0, maximum: 100 },
    signals: {
      type: 'array',
      items: { type: 'string', enum: [...Signal.options] },
    },
    evidence: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          quote: { type: 'string' },
          signal: { type: 'string', enum: [...Signal.options] },
        },
        required: ['quote', 'signal'],
      },
    },
    rationale: { type: 'string' },
    confidence: { type: 'number', minimum: 0, maximum: 1 },
  },
  required: ['score', 'signals', 'evidence', 'rationale', 'confidence'],
} as const;
